import { z } from "zod";

// ─── Locked safety bounds (server-enforced, displayed but not editable) ─────
// Hard ceilings/floors that an IT admin cannot push past from the UI. Any
// PATCH that violates these is rejected with HTTP 400 by the route handler.
// Visible in the tab so IT can see the boundary exists, builds trust.
export const RUNTIME_CONFIG_LOCKED_BOUNDS = {
  /** Smallest allowed non-zero refresh interval per tier (seconds). */
  minRefreshSeconds: 10,
  /** Largest allowed refresh interval per tier (seconds). 24h. */
  maxRefreshSeconds: 86400,
  /** Tier-aware hard cap on concurrent background fetches per browser. */
  maxConcurrentPerUser: 4,
  /** Server-side hard cap on total background traffic (informational). */
  maxTotalRequestsPerMinute: 500,
} as const;

// ─── Tier model ─────────────────────────────────────────────────────────────
// Three tiers cover every list/page in the app. The frontend QueryClient
// classifies each query by URL prefix and applies the matching cadence.
export const RUNTIME_CONFIG_TIERS = ["liveWork", "reference", "configuration"] as const;
export type RuntimeConfigTier = typeof RUNTIME_CONFIG_TIERS[number];

export const RUNTIME_CONFIG_TIER_LABELS: Record<RuntimeConfigTier, string> = {
  liveWork: "Live work data",
  reference: "Reference data",
  configuration: "Configuration",
};

export const RUNTIME_CONFIG_TIER_DESCRIPTIONS: Record<RuntimeConfigTier, string> = {
  liveWork:
    "Action items, hand-offs, MD review queue, awaiting-dispatch, sub-assignments. The hot collaborative data users edit during their working day.",
  reference:
    "KPI dashboard, audit trail, reports. Useful but not urgent — changes infrequently within a session.",
  configuration:
    "Users, team members, governance lanes, branding, SMTP. Essentially static during a working day.",
};

// ─── Presets ────────────────────────────────────────────────────────────────
export const RUNTIME_CONFIG_PRESETS = ["off", "conservative", "balanced", "real_time", "custom"] as const;
export type RuntimeConfigPreset = typeof RUNTIME_CONFIG_PRESETS[number];

export const RUNTIME_CONFIG_PRESET_LABELS: Record<RuntimeConfigPreset, string> = {
  off: "Off",
  conservative: "Conservative (recommended for WiFi / VPN)",
  balanced: "Balanced",
  real_time: "Real-time",
  custom: "Custom",
};

export const RUNTIME_CONFIG_PRESET_DESCRIPTIONS: Record<RuntimeConfigPreset, string> = {
  off: "No auto-refresh. Users press F5 to see updates. Lowest server load and zero background traffic. Use during a maintenance window or when the server is under stress.",
  conservative: "Refreshes live work every 2 minutes. Best fit for laptops on company WiFi or remote staff on the company VPN — gentle on flaky links and round-trip latency, still fresh enough for governance work.",
  balanced: "Refreshes live work every 30 seconds. Suitable when most users are on a stable wired LAN inside the office. Can feel chatty over WiFi or VPN.",
  real_time: "Refreshes live work every 10 seconds. Reserve for high-tempo periods on a stable LAN. Not recommended for VPN users — round-trip latency can pile requests up.",
  custom: "Hand-tuned values from the Advanced section.",
};

export const RUNTIME_CONFIG_APPLY_METHODS = ["refresh_required", "live_broadcast"] as const;
export type RuntimeConfigApplyMethod = typeof RUNTIME_CONFIG_APPLY_METHODS[number];

export const RUNTIME_CONFIG_ANNOUNCEMENT_STYLES = ["subtle_dot", "banner", "none"] as const;
export type RuntimeConfigAnnouncementStyle = typeof RUNTIME_CONFIG_ANNOUNCEMENT_STYLES[number];

// ─── Settings shape ─────────────────────────────────────────────────────────
export interface RuntimeConfigSettings {
  preset: RuntimeConfigPreset;
  /** Per-tier refresh interval in seconds. 0 = disabled (no auto-refresh). */
  refreshIntervals: Record<RuntimeConfigTier, number>;
  refetchOnFocus: boolean;
  refetchInBackground: boolean;
  refetchOnReconnect: boolean;
  applyMethod: RuntimeConfigApplyMethod;
  notifications: {
    showRefreshToast: boolean;
    soundOnAssignmentChange: boolean;
    refreshAnnouncementStyle: RuntimeConfigAnnouncementStyle;
  };
  /**
   * Phase 4 of Handoff Deadlines — toggleable KPI grading.
   * When true, the on-time / missed counts on the KPI dashboard and PDF
   * grade closures against `currentInternalDueDate ?? dueDate` (the
   * effective deadline) instead of the official board `dueDate`.
   * Default false (board date) preserves the historical KPI definition
   * for any deployment that doesn't opt in. The "On Original Due"
   * column is unaffected — it always grades against the original
   * board date set at item creation.
   * Optional in the type so existing rows in `v2_runtime_config`
   * (written before this field existed) still parse cleanly; treated
   * as false when absent.
   */
  kpiGradeAgainstInternalDeadline?: boolean;
}

// ─── Preset values ──────────────────────────────────────────────────────────
const PRESET_VALUES: Record<Exclude<RuntimeConfigPreset, "custom">, RuntimeConfigSettings> = {
  off: {
    preset: "off",
    refreshIntervals: { liveWork: 0, reference: 0, configuration: 0 },
    refetchOnFocus: true,
    refetchInBackground: false,
    refetchOnReconnect: true,
    applyMethod: "refresh_required",
    notifications: {
      showRefreshToast: false,
      soundOnAssignmentChange: false,
      refreshAnnouncementStyle: "none",
    },
    kpiGradeAgainstInternalDeadline: false,
  },
  conservative: {
    preset: "conservative",
    refreshIntervals: { liveWork: 120, reference: 300, configuration: 900 },
    refetchOnFocus: true,
    refetchInBackground: false,
    refetchOnReconnect: true,
    applyMethod: "refresh_required",
    notifications: {
      showRefreshToast: false,
      soundOnAssignmentChange: false,
      refreshAnnouncementStyle: "subtle_dot",
    },
    kpiGradeAgainstInternalDeadline: false,
  },
  balanced: {
    preset: "balanced",
    refreshIntervals: { liveWork: 30, reference: 120, configuration: 900 },
    refetchOnFocus: true,
    refetchInBackground: false,
    refetchOnReconnect: true,
    applyMethod: "refresh_required",
    notifications: {
      showRefreshToast: false,
      soundOnAssignmentChange: false,
      refreshAnnouncementStyle: "subtle_dot",
    },
    kpiGradeAgainstInternalDeadline: false,
  },
  real_time: {
    preset: "real_time",
    refreshIntervals: { liveWork: 10, reference: 60, configuration: 900 },
    refetchOnFocus: true,
    refetchInBackground: false,
    refetchOnReconnect: true,
    applyMethod: "refresh_required",
    notifications: {
      showRefreshToast: true,
      soundOnAssignmentChange: false,
      refreshAnnouncementStyle: "subtle_dot",
    },
    kpiGradeAgainstInternalDeadline: false,
  },
};

export function getPresetSettings(preset: Exclude<RuntimeConfigPreset, "custom">): RuntimeConfigSettings {
  // Deep-clone so callers can mutate without poisoning the constant.
  return JSON.parse(JSON.stringify(PRESET_VALUES[preset]));
}

/**
 * The factory default for every fresh install and the "Reset" button.
 *
 * Conservative was chosen over Balanced because the typical deployment is
 * 40–50 users on company WiFi with a meaningful fraction connecting from
 * VPN while travelling. A 30-second poll over a 500–800ms VPN round-trip
 * piles requests up and drains laptop battery for marginal freshness gain.
 * IT admins can move to Balanced or Real-time from the Settings tab if
 * their network conditions warrant it.
 */
export const RUNTIME_CONFIG_DEFAULT_SETTINGS: RuntimeConfigSettings = getPresetSettings("conservative");

// ─── Zod validator (server-side gate) ───────────────────────────────────────
const intervalSchema = z
  .number()
  .int()
  .nonnegative()
  .refine(
    (v) => v === 0 || v >= RUNTIME_CONFIG_LOCKED_BOUNDS.minRefreshSeconds,
    {
      message: `Refresh interval must be 0 (off) or at least ${RUNTIME_CONFIG_LOCKED_BOUNDS.minRefreshSeconds} seconds (locked safety bound).`,
    },
  )
  .refine(
    (v) => v <= RUNTIME_CONFIG_LOCKED_BOUNDS.maxRefreshSeconds,
    {
      message: `Refresh interval must be at most ${RUNTIME_CONFIG_LOCKED_BOUNDS.maxRefreshSeconds} seconds (24h).`,
    },
  );

export const runtimeConfigSettingsSchema: z.ZodType<RuntimeConfigSettings> = z.object({
  preset: z.enum(RUNTIME_CONFIG_PRESETS),
  refreshIntervals: z.object({
    liveWork: intervalSchema,
    reference: intervalSchema,
    configuration: intervalSchema,
  }),
  refetchOnFocus: z.boolean(),
  refetchInBackground: z.boolean(),
  refetchOnReconnect: z.boolean(),
  applyMethod: z.enum(RUNTIME_CONFIG_APPLY_METHODS),
  notifications: z.object({
    showRefreshToast: z.boolean(),
    soundOnAssignmentChange: z.boolean(),
    refreshAnnouncementStyle: z.enum(RUNTIME_CONFIG_ANNOUNCEMENT_STYLES),
  }),
  // Optional with no default — undefined / missing is treated as false
  // by every consumer, so rows persisted before Phase 4 stay valid.
  kpiGradeAgainstInternalDeadline: z.boolean().optional(),
});

export const runtimeConfigPatchSchema = z.object({
  settings: runtimeConfigSettingsSchema,
  reason: z.string().trim().max(500).optional(),
});

// ─── Tier classification (frontend uses this to bucket queries) ─────────────
// URL-prefix-based. New endpoints default to "reference" — the safest middle
// tier. To classify a new endpoint explicitly, add it to the appropriate list.
const LIVE_WORK_PREFIXES = [
  "/api/action-items",
  "/api/sub-assignments",
  "/api/md-meetings",
  "/api/awaiting-dispatch",
  "/api/handoffs",
];

const CONFIGURATION_PREFIXES = [
  "/api/admin",
  "/api/users",
  "/api/team-members",
  "/api/governance",
  "/api/branding",
  "/api/smtp",
  "/api/runtime-config",
  "/api/permissions",
  "/api/roles",
  "/api/sla-config",
  "/api/auth",
  "/api/me",
];

export function classifyQueryTier(queryKey: readonly unknown[]): RuntimeConfigTier {
  const first = queryKey[0];
  if (typeof first !== "string") return "reference";
  for (const p of LIVE_WORK_PREFIXES) {
    if (first.startsWith(p)) return "liveWork";
  }
  for (const p of CONFIGURATION_PREFIXES) {
    if (first.startsWith(p)) return "configuration";
  }
  return "reference";
}

// ─── Projected-load math (used by the Diagnostics section) ──────────────────
/**
 * Estimate background requests/minute under the given settings and connected
 * user count. Each tier contributes (60 / interval) requests per query type
 * per user. We assume 4 hot live-work queries, 2 reference queries, 1 config
 * query per active user — these constants are conservative averages from the
 * current screen mix.
 */
export function projectedLoadPerMinute(settings: RuntimeConfigSettings, userCount: number): number {
  const queriesPerTier: Record<RuntimeConfigTier, number> = {
    liveWork: 4,
    reference: 2,
    configuration: 1,
  };
  let total = 0;
  for (const tier of RUNTIME_CONFIG_TIERS) {
    const seconds = settings.refreshIntervals[tier];
    if (seconds <= 0) continue;
    total += (60 / seconds) * queriesPerTier[tier] * userCount;
  }
  return Math.round(total);
}
